/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import {
  dismissStoreActivation,
  isStoreActivationDismissed,
  isStoreActivationSetupStarted,
  startStoreActivationSetup,
} from '../application/onboardingUiStorage';
import { hoursConfigured, computeDeliveryDone } from '../application/storeActivationEvidence';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { usePublicStoreFeaturesVisible } from '../../shell/feature-flags/usePublicStoreFeaturesVisible';
import { useDismissableCard } from './useDismissableCard';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useHomeOnboardingCardsContext } from '../application/HomeOnboardingCardsContext';
import { subscribeCatalogSynced } from '../../sync/core/catalogSyncListeners';
import { branchMissingMapLocation } from '../../settings/domain/branchLocationStatus';
import {
  getStoreProfile,
  updateStoreProfile,
} from '../../settings/infra/storeProfileRepo';
import { storeProfileToDraft } from '../../settings/domain/storeProfileTypes';
import { showSuccess } from '../../shared/ui/appToast';

type Props = {
  userId: string;
  /** Solo dueño ve la oferta de tienda. */
  isOwner?: boolean;
  /**
   * False en día 1 fresh: no mostrar nudge ni checklist.
   * True después de primera venta / camino día 1 cerrado.
   */
  offerAllowed?: boolean;
};

type StepId = 'location' | 'hours' | 'delivery' | 'online';

type Step = {
  id: StepId;
  label: string;
  hint: string;
  route: string;
  done: boolean;
};

type Mode = 'nudge' | 'checklist';

/** Oferta de tienda online: nudge dismissible → checklist solo tras opt-in. */
export function StoreActivationChecklistCard({
  userId,
  isOwner = true,
  offerAllowed = true,
}: Props) {
  const navigation = useNavigation<any>();
  const branchId = useSessionStore((s) => s.currentBranchId);
  const branch = useSessionStore((s) => s.branches.find((b) => b.id === s.currentBranchId));
  const publicStoreOn = usePublicStoreFeaturesVisible();
  const canBranch = useHasBusinessCapability('employees');
  const hasDelivery = useHasBusinessCapability('delivery_fulfillment');
  const hasAppointments = useHasBusinessCapability('appointments');
  const { visible, onDismiss } = useDismissableCard(
    userId,
    isStoreActivationDismissed,
    dismissStoreActivation,
  );

  const [mode, setMode] = useState<Mode>('nudge');
  const [publishing, setPublishing] = useState(false);

  // Evidencia compartida del provider: home_snapshot decide, y si la card puede
  // mostrarse llega store_activation_snapshot con profile/zones/online count.
  const evidence = useHomeOnboardingCardsContext()?.evidence;
  const activation = evidence?.activation ?? null;
  const profile = activation?.profile ?? null;
  const zonesCount = activation?.zonesCount ?? 0;
  const onlineCount = activation?.onlineVisibleProducts ?? null;
  const loading = evidence?.loading === true;
  const initialized = evidence?.loaded === true;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const started = await isStoreActivationSetupStarted(userId);
      if (!cancelled && started) setMode('checklist');
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Re-correr reload cuando el catálogo synca — el paso 4 (productos visibles
  // online) puede cambiar server-side tras un push de visibilidad.
  useEffect(() => {
    return subscribeCatalogSynced(() => {
      void evidence?.reload({ force: true });
    });
  }, [evidence]);

  const locationDone = branch ? !branchMissingMapLocation(branch) : false;
  const hoursDone = hoursConfigured(profile);
  const deliveryDone = computeDeliveryDone(hasDelivery, profile, zonesCount);
  const onlineDone = onlineCount !== null && onlineCount > 0;

  const steps = useMemo((): Step[] => {
    if (!publicStoreOn) return [];
    return [
      {
        id: 'location',
        label: '1. Ubicación',
        hint: locationDone ? 'Listo' : 'Buscá la dirección del local y confirmala en el mapa.',
        route: 'BranchIdentity',
        done: locationDone,
      },
      {
        id: 'hours',
        label: '2. Horarios',
        hint: hoursDone ? 'Listo' : 'Definí cuándo te pueden pedir',
        route: 'StorefrontHub',
        done: hoursDone,
      },
      {
        id: 'delivery',
        label: '3. Cómo entregan',
        hint: deliveryDone
          ? profile?.deliveryEnabled
            ? 'Listo'
            : 'Solo retiro. Listo.'
          : 'Retiro en el local y/o delivery tuyo. Sin envíos de Vuelto por ahora.',
        route: hasDelivery ? 'DeliverySettings' : 'StorefrontHub',
        done: deliveryDone,
      },
      {
        id: 'online',
        label: '4. Qué se ve online',
        hint: onlineDone
          ? 'Listo'
          : 'En Catálogo, marcá al menos un producto o servicio como visible online.',
        route: 'Catalog',
        done: onlineDone,
      },
    ];
  }, [
    publicStoreOn,
    locationDone,
    hoursDone,
    deliveryDone,
    onlineDone,
    profile?.deliveryEnabled,
    hasDelivery,
  ]);

  const requiredDone = locationDone && hoursDone && deliveryDone && onlineDone;
  const allDone = requiredDone && Boolean(profile?.isPublic);

  const go = useCallback(
    (route: string) => {
      navigateInShell(navigation, route);
    },
    [navigation],
  );

  const beginSetup = useCallback(async () => {
    await startStoreActivationSetup(userId);
    setMode('checklist');
  }, [userId]);

  const publish = useCallback(async () => {
    if (!branchId || !profile || !requiredDone) return;
    setPublishing(true);
    try {
      // Write path: re-lee el perfil completo fresco — el snapshot de activación
      // solo trae los campos de evidencia, no el draft entero.
      const full = await getStoreProfile(branchId);
      if (!full) throw new Error('store profile missing');
      const draft = storeProfileToDraft(full);
      draft.isPublic = true;
      await updateStoreProfile(branchId, draft);
      showSuccess(
        'Tienda publicada',
        hasAppointments && !hasDelivery
          ? 'Ya pueden reservar turnos online.'
          : 'Ya pueden pedirte online.',
      );
      await onDismiss();
      void evidence?.reload({ force: true });
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'onboarding', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo publicar.' });
    } finally {
      setPublishing(false);
    }
  }, [branchId, profile, requiredDone, onDismiss, evidence, hasAppointments, hasDelivery]);

  if (!isOwner || !offerAllowed || !visible || !publicStoreOn || !canBranch) return null;
  if (!initialized || onlineCount === null) return null;
  if (profile?.isPublic || allDone) return null;

  if (mode === 'nudge') {
    return (
      <View style={[styles.card, styles.offerCard]} testID="home.store-activation">
        <View style={styles.header}>
          <Text style={styles.title}>Publicá tu tienda online</Text>
          <TouchableOpacity
            onPress={() => void onDismiss()}
            accessibilityRole="button"
            accessibilityLabel="Cerrar"
          >
            <Ionicons name="close" size={18} color={ticketColors.ink2} />
          </TouchableOpacity>
        </View>
        <View style={styles.bullets}>
          <Text style={styles.bullet}>
            · Se genera un enlace para compartir. El cliente ve tu catálogo y te compra.
          </Text>
          <Text style={styles.bullet}>
            · Por ahora no hay envíos de Vuelto: podés ofrecer retiro en el local o delivery tuyo.
          </Text>
          <Text style={styles.bullet}>
            · Si después conectás Mercado Pago, también cobrás online.
          </Text>
        </View>
        <TouchableOpacity
          style={styles.ghostBtn}
          onPress={() => void beginSetup()}
          accessibilityRole="button"
          accessibilityLabel="Configurar mi tienda"
        >
          <Text style={styles.ghostBtnText}>Configurar mi tienda</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.card} testID="home.store-activation">
      <View style={styles.header}>
        <Text style={styles.title}>Publicá tu tienda online</Text>
        <TouchableOpacity
          onPress={() => void onDismiss()}
          accessibilityRole="button"
          accessibilityLabel="Cerrar"
        >
          <Ionicons name="close" size={18} color={ticketColors.ink2} />
        </TouchableOpacity>
      </View>
      {loading && !profile ? (
        <ActivityIndicator color={ticketColors.gold} />
      ) : (
        steps.map((step) => (
          <TouchableOpacity
            key={step.id}
            style={styles.row}
            onPress={() => go(step.route)}
            accessibilityRole="button"
            accessibilityLabel={step.label}
          >
            <Ionicons
              name={step.done ? 'checkmark-circle' : 'ellipse-outline'}
              size={20}
              color={step.done ? ticketColors.ok : ticketColors.ink2}
            />
            <View style={styles.rowText}>
              <Text style={styles.rowTitle}>{step.label}</Text>
              <Text style={styles.rowHint}>{step.hint}</Text>
            </View>
            <Ionicons name="chevron-forward" size={18} color={ticketColors.ink2} />
          </TouchableOpacity>
        ))
      )}
      <TouchableOpacity
        style={[
          styles.publishBtn,
          (!requiredDone || publishing || profile?.isPublic) && styles.publishDisabled,
        ]}
        onPress={() => void publish()}
        disabled={!requiredDone || publishing || Boolean(profile?.isPublic)}
        accessibilityRole="button"
      >
        <Text style={styles.publishText}>{publishing ? 'Publicando…' : 'Publicar tienda'}</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.mpRow}
        onPress={() => go('IntegrationsHub')}
        accessibilityRole="button"
        accessibilityLabel="Conectá Mercado Pago para cobrar online (opcional)"
      >
        <Text style={styles.mpText}>Conectá Mercado Pago para cobrar online (opcional)</Text>
        <Text style={styles.mpChevron}>›</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    ...ticketSurfaces.paperSheet,
    gap: spacing.sm,
    padding: spacing.md,
  },
  offerCard: {
    borderStyle: 'dashed',
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 15,
    fontWeight: '800',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    flex: 1,
    paddingRight: spacing.sm,
  },
  bullets: {
    gap: 6,
  },
  bullet: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    lineHeight: 18,
  },
  ghostBtn: {
    marginTop: 4,
    alignItems: 'center',
    paddingVertical: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  ghostBtnText: {
    fontSize: 14,
    fontWeight: '800',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: ticketColors.ruleSoft,
  },
  rowText: { flex: 1, minWidth: 0, gap: 2 },
  rowTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  rowHint: {
    fontSize: 12,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  publishBtn: {
    marginTop: 4,
    alignItems: 'center',
    paddingVertical: 12,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
  },
  publishDisabled: { opacity: 0.45 },
  publishText: {
    fontSize: 14,
    fontWeight: '800',
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.sans,
  },
  mpRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  mpText: {
    flex: 1,
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  mpChevron: {
    fontSize: 18,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    paddingLeft: spacing.sm,
  },
});
